// src/utils/generateCoverLetterDoc.js
// Cover letter downloads: Word (.docx via XML), PDF (print dialog), HTML, TXT

// ── Trigger browser download ──
function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a   = Object.assign(document.createElement("a"), { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function formatDate() {
  return new Date().toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });
}

function buildParagraphs(letter) {
  return [letter.opening, letter.body1, letter.body2, letter.closing].filter(Boolean);
}

// ═══════════════════════════════════════════════
// TXT
// ═══════════════════════════════════════════════
export function downloadCoverLetterAsTxt(letter, fileName = "cover-letter.txt") {
  const paras = buildParagraphs(letter);
  const text  = [
    formatDate(), "",
    letter.salutation || "Dear Hiring Manager,", "",
    ...paras.map(p => p + "\n"),
    letter.sign_off || "Sincerely,", "",
    "___________________",
  ].join("\n");
  triggerDownload(new Blob([text], { type: "text/plain;charset=utf-8" }), fileName);
}

// ═══════════════════════════════════════════════
// HTML — clean, print-ready
// ═══════════════════════════════════════════════
function buildHtml(letter, userName = "", userEmail = "") {
  const paras = buildParagraphs(letter).map(p => `    <p>${p}</p>`).join("\n");
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <title>Cover Letter</title>
  <style>
    body { font-family: Georgia, 'Times New Roman', serif; max-width: 720px; margin: 60px auto; padding: 0 48px; color: #1a1a1a; line-height: 1.9; font-size: 14pt; }
    .date { font-family: Arial, sans-serif; font-size: 11pt; color: #555; margin-bottom: 32px; }
    .salutation { font-family: Arial, sans-serif; font-weight: 600; margin-bottom: 20px; }
    p { margin: 0 0 18px 0; text-align: justify; }
    .sign-off { font-family: Arial, sans-serif; font-weight: 600; margin-top: 36px; }
    .sig-name { font-family: Arial, sans-serif; font-weight: 700; margin-top: 8px; }
    .sig-email { font-family: Arial, sans-serif; font-size: 11pt; color: #555; }
    @media print { body { margin: 40px; } }
  </style>
</head>
<body>
  <div class="date">${formatDate()}</div>
  <div class="salutation">${letter.salutation || "Dear Hiring Manager,"}</div>
${paras}
  <div class="sign-off">${letter.sign_off || "Sincerely,"}</div>
  <br/>
  <div class="sig-name">${userName || "___________________"}</div>
  ${userEmail ? `<div class="sig-email">${userEmail}</div>` : ""}
</body>
</html>`;
}

export function downloadCoverLetterAsHtml(letter, fileName = "cover-letter.html", userName = "", userEmail = "") {
  triggerDownload(
    new Blob([buildHtml(letter, userName, userEmail)], { type: "text/html;charset=utf-8" }),
    fileName
  );
}

// ═══════════════════════════════════════════════
// WORD — genuine .docx using Office Open XML
// No external library needed — pure XML in a zip-like structure
// Word opens this natively without any conversion
// ═══════════════════════════════════════════════
export async function downloadCoverLetterAsWord(letter, fileName = "cover-letter.docx", userName = "", userEmail = "") {
  try {
    const paras  = buildParagraphs(letter);

    // Build Word XML paragraphs
    function wPara(text, opts = {}) {
      const { bold = false, size = 24, color = "1a1a1a", spaceAfter = 200, font = "Calibri" } = opts;
      if (!text) return "";
      // Escape XML special chars
      const safe = text.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
      return `
    <w:p>
      <w:pPr>
        <w:spacing w:after="${spaceAfter}" w:line="360" w:lineRule="auto"/>
        <w:jc w:val="both"/>
      </w:pPr>
      <w:r>
        <w:rPr>
          ${bold ? "<w:b/>" : ""}
          <w:sz w:val="${size}"/>
          <w:szCs w:val="${size}"/>
          <w:color w:val="${color}"/>
          <w:rFonts w:ascii="${font}" w:hAnsi="${font}"/>
        </w:rPr>
        <w:t xml:space="preserve">${safe}</w:t>
      </w:r>
    </w:p>`;
    }

    function wEmptyLine() {
      return `
    <w:p><w:pPr><w:spacing w:after="0"/></w:pPr></w:p>`;
    }

    const bodyXml = [
      wPara(formatDate(),                      { size: 20, color: "555555", spaceAfter: 400 }),
      wPara(letter.salutation || "Dear Hiring Manager,", { bold: true, size: 24, spaceAfter: 300 }),
      ...paras.map(p => wPara(p,              { size: 24, spaceAfter: 280 })),
      wEmptyLine(),
      wPara(letter.sign_off || "Sincerely,",  { bold: true, size: 24, spaceAfter: 120 }),
      wEmptyLine(),
      wEmptyLine(),
      wEmptyLine(),
      wPara(userName || "___________________",{ bold: true, size: 24, spaceAfter: 80 }),
      userEmail ? wPara(userEmail,            { size: 20, color: "555555", spaceAfter: 0 }) : "",
    ].join("");

    const docXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:wpc="http://schemas.microsoft.com/office/word/2010/wordprocessingCanvas"
  xmlns:mc="http://schemas.openxmlformats.org/markup-compatibility/2006"
  xmlns:o="urn:schemas-microsoft-com:office:office"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
  xmlns:m="http://schemas.openxmlformats.org/officeDocument/2006/math"
  xmlns:v="urn:schemas-microsoft-com:vml"
  xmlns:wp14="http://schemas.microsoft.com/office/word/2010/wordprocessingDrawing"
  xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing"
  xmlns:w10="urn:schemas-microsoft-com:office:word"
  xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
  xmlns:w14="http://schemas.microsoft.com/office/word/2010/wordml"
  xmlns:wpg="http://schemas.microsoft.com/office/word/2010/wordprocessingGroup"
  xmlns:wpi="http://schemas.microsoft.com/office/word/2010/wordprocessingInk"
  xmlns:wne="http://schemas.microsoft.com/office/word/2006/wordml"
  xmlns:wps="http://schemas.microsoft.com/office/word/2010/wordprocessingShape"
  mc:Ignorable="w14 wp14">
  <w:body>
${bodyXml}
    <w:sectPr>
      <w:pgSz w:w="12240" w:h="15840"/>
      <w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440"
               w:header="708" w:footer="708" w:gutter="0"/>
    </w:sectPr>
  </w:body>
</w:document>`;

    const relsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

    const stylesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:docDefaults>
    <w:rPrDefault>
      <w:rPr>
        <w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/>
        <w:sz w:val="24"/>
        <w:szCs w:val="24"/>
        <w:lang w:val="en-US"/>
      </w:rPr>
    </w:rPrDefault>
  </w:docDefaults>
</w:styles>`;

    const contentTypesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
</Types>`;

    const rootRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`;

    // ── Build .docx zip using JSZip (loaded from CDN if not available) ──
    const JSZip = await loadJSZip();
    const zip   = new JSZip();

    zip.file("[Content_Types].xml", contentTypesXml);
    zip.file("_rels/.rels",          rootRelsXml);
    zip.file("word/document.xml",    docXml);
    zip.file("word/styles.xml",      stylesXml);
    zip.file("word/_rels/document.xml.rels", relsXml);

    const blob = await zip.generateAsync({
      type:               "blob",
      mimeType:           "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      compression:        "DEFLATE",
      compressionOptions: { level: 6 },
    });

    triggerDownload(blob, fileName);
  } catch (err) {
    console.error("Word download failed, falling back to HTML:", err);
    downloadCoverLetterAsHtml(letter, fileName.replace(".docx", ".html"), userName, userEmail);
  }
}

// ── Load JSZip from CDN ──
function loadJSZip() {
  return new Promise((resolve, reject) => {
    if (window.JSZip) { resolve(window.JSZip); return; }
    const s    = document.createElement("script");
    s.src      = "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
    s.onload   = () => resolve(window.JSZip);
    s.onerror  = () => reject(new Error("JSZip failed to load"));
    document.head.appendChild(s);
  });
}

// ═══════════════════════════════════════════════
// PDF — browser print dialog → Save as PDF
// Most reliable cross-platform approach
// ═══════════════════════════════════════════════
export function downloadCoverLetterAsPdf(letter, userName = "", userEmail = "") {
  const html    = buildHtml(letter, userName, userEmail);
  const newTab  = window.open("", "_blank");
  if (!newTab) {
    // Popup blocked — fall back to HTML download with instructions
    downloadCoverLetterAsHtml(letter, "cover-letter-print-as-pdf.html", userName, userEmail);
    alert("Popup blocked. The HTML file has been downloaded. Open it in your browser and press Ctrl+P → Save as PDF.");
    return;
  }
  newTab.document.write(html);
  newTab.document.close();
  setTimeout(() => {
    newTab.focus();
    newTab.print();
  }, 600);
}
